/**
 * Terms & Conditions Editor
 *
 * Component for managing terms and conditions items in a template.
 * Technicians must acknowledge these before starting work.
 */

import { useState } from 'react';
import {
  Plus,
  GripVertical,
  Trash2,
  ChevronDown,
  ChevronUp,
  FileText,
  PenLine,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { TemplateTermsItem } from '../types';

interface TermsEditorProps {
  items: TemplateTermsItem[];
  onChange: (items: TemplateTermsItem[]) => void;
}

const generateKey = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .substring(0, 50);
};

export function TermsEditor({ items, onChange }: TermsEditorProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [newItem, setNewItem] = useState<Partial<TemplateTermsItem> | null>(null);

  const handleAddItem = () => {
    setNewItem({
      key: '',
      title: '',
      content: '',
      requiresSignature: false,
      sortOrder: items.length,
    });
  };

  const handleSaveNewItem = () => {
    if (!newItem || !newItem.title?.trim() || !newItem.content?.trim()) return;

    const key = newItem.key?.trim() || generateKey(newItem.title);

    const item: TemplateTermsItem = {
      key,
      title: newItem.title.trim(),
      content: newItem.content.trim(),
      requiresSignature: newItem.requiresSignature ?? false,
      sortOrder: items.length,
    };

    onChange([...items, item]);
    setNewItem(null);
  };

  const handleCancelNewItem = () => {
    setNewItem(null);
  };

  const handleUpdateItem = (index: number, updates: Partial<TemplateTermsItem>) => {
    const updated = [...items];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const handleDeleteItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    updated.forEach((item, i) => {
      item.sortOrder = i;
    });
    onChange(updated);
    if (expandedIndex === index) setExpandedIndex(null);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...items];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updated.forEach((item, i) => {
      item.sortOrder = i;
    });
    onChange(updated);
    setExpandedIndex(index - 1);
  };

  const handleMoveDown = (index: number) => {
    if (index === items.length - 1) return;
    const updated = [...items];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updated.forEach((item, i) => {
      item.sortOrder = i;
    });
    onChange(updated);
    setExpandedIndex(index + 1);
  };

  const truncateContent = (content: string, maxLength = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Define safety protocols and compliance rules technicians must acknowledge.
        </p>
        <Button size="sm" onClick={handleAddItem} disabled={!!newItem}>
          <Plus className="h-4 w-4 mr-2" />
          Add Term
        </Button>
      </div>

      {/* Items List */}
      <div className="space-y-2">
        {items.map((item, index) => (
          <Collapsible
            key={`${item.key}-${index}`}
            open={expandedIndex === index}
            onOpenChange={(open) => setExpandedIndex(open ? index : null)}
          >
            <Card>
              <div className="flex items-center gap-2 p-3">
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === items.length - 1}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>

                <GripVertical className="h-4 w-4 text-muted-foreground" />

                <CollapsibleTrigger asChild>
                  <div className="flex-1 flex items-center gap-3 cursor-pointer">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.title}</span>
                        {item.requiresSignature && (
                          <Badge variant="secondary" className="text-xs">
                            <PenLine className="h-3 w-3 mr-1" />
                            Signature
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {truncateContent(item.content)}
                      </span>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </div>
                </CollapsibleTrigger>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => handleDeleteItem(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <CollapsibleContent>
                <div className="border-t px-3 py-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={item.title}
                        onChange={(e) =>
                          handleUpdateItem(index, { title: e.target.value })
                        }
                        placeholder="e.g., Safety Protocol"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Key</Label>
                      <Input
                        value={item.key}
                        onChange={(e) =>
                          handleUpdateItem(index, { key: e.target.value })
                        }
                        placeholder="Auto-generated from title"
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Content *</Label>
                    <Textarea
                      value={item.content}
                      onChange={(e) =>
                        handleUpdateItem(index, { content: e.target.value })
                      }
                      placeholder="Enter the terms and conditions content. Technicians will need to read and acknowledge this before proceeding."
                      rows={6}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Supports plain text. Use line breaks for paragraphs.
                    </p>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label htmlFor={`signature-${index}`}>Requires Signature</Label>
                      <p className="text-sm text-muted-foreground">
                        Technician must provide a digital signature to acknowledge
                      </p>
                    </div>
                    <Switch
                      id={`signature-${index}`}
                      checked={item.requiresSignature}
                      onCheckedChange={(checked) =>
                        handleUpdateItem(index, { requiresSignature: checked })
                      }
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        ))}

        {/* New Item Form */}
        {newItem && (
          <Card className="border-primary">
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span className="font-medium">New Term</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={newItem.title || ''}
                    onChange={(e) =>
                      setNewItem({ ...newItem, title: e.target.value })
                    }
                    placeholder="e.g., Safety Protocol"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label>Key</Label>
                  <Input
                    value={newItem.key || ''}
                    onChange={(e) =>
                      setNewItem({ ...newItem, key: e.target.value })
                    }
                    placeholder="Auto-generated from title"
                    className="font-mono text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Content *</Label>
                <Textarea
                  value={newItem.content || ''}
                  onChange={(e) =>
                    setNewItem({ ...newItem, content: e.target.value })
                  }
                  placeholder="Enter the terms and conditions content. Technicians will need to read and acknowledge this before proceeding."
                  rows={6}
                  className="font-mono text-sm"
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label>Requires Signature</Label>
                  <p className="text-sm text-muted-foreground">
                    Technician must provide a digital signature to acknowledge
                  </p>
                </div>
                <Switch
                  checked={newItem.requiresSignature ?? false}
                  onCheckedChange={(checked) =>
                    setNewItem({ ...newItem, requiresSignature: checked })
                  }
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={handleCancelNewItem}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveNewItem}
                  disabled={!newItem.title?.trim() || !newItem.content?.trim()}
                >
                  Add Term
                </Button>
              </div>
            </div>
          </Card>
        )}

        {items.length === 0 && !newItem && (
          <div className="text-center py-8 text-muted-foreground">
            No terms defined. Click "Add Term" to add safety protocols or compliance rules.
          </div>
        )}
      </div>
    </div>
  );
}
